import React, { useState } from "react";
import { GetWSMgr } from "../Utils/StateLib";
import { AppendTableRecordEditorModal } from "@/appComponents/Blocks/Modals/RecordEditorModal/AppendTableRecordEditorModal";
import { AppendTableCreatePositionModal } from "@/appComponents/Blocks/Modals/RecordEditorModal/AppendTableCreatePositionModal";
import { AppendTablePositionEditorModal } from "@/appComponents/Blocks/Modals/RecordEditorModal/AppendTablePositionEditorModal";
import { AppendTableEditorModal } from "@/appComponents/Blocks/Modals/SettingsModal/ChartSettings/DataSourceConfiguration/DataSources/AppendTable/AppendTableEditorModal";
import { ManageAppendTablesModal } from "@/appComponents/Blocks/Modals/SettingsModal/ChartSettings/DataSourceConfiguration/DataSources/AppendTable/ManageAppendTablesModal";
import { BuildAppendTableRFC, UpdateChartMetadata } from "@/Utils/Promises";
import { appstr } from "@coreapp/utils/appstr";
import { QTableManager } from "@coreapp/CoreApp/QTableManager";
import { QTableChange } from "@coreapp/CoreApp/QTableChange";
import { SerializeOverrideObj } from "@coreapp/utils/SerializeOverrideObj";
import { CreateAppendTableModal } from "@/appComponents/Blocks/Modals/SettingsModal/ChartSettings/DataSourceConfiguration/DataSources/AppendTable/CreateAppendTableModal";
import { GatedModals } from "@/appComponents/Blocks/Composites/ModalHost/GatedModals";
import { useTopic } from "@/hooks/useTopic";
import { useToast } from "@/providers/ToastProvider";
import { QManageTablesRFC } from "@coreapp/CoreApp/QManageTablesRFC";
import { Globals } from "@coreapp/CoreApp/Globals";
import { useChartSettingsContext } from "@/providers/ChartSettingsProvider";
import { GetNeedRefreshDiagnostics } from "@/appComponents/Blocks/Modals/SettingsModal/ChartSettings/Utils";
import {
  enumDataSource,
  enumIWizDataSet,
  enumObjectTypes,
  enumOverrideType
} from "@coreapp/constants";
import { cloneDeep } from "lodash";
import { QXDocMgr } from "@coreapp/CoreApp/QXDocMgr";
import { QIWizData } from "@coreapp/CoreApp/QIWizData";

const TableManagerContext = React.createContext();

const TableManagerProvider = ({ children }) => {
  const { chartConfigRef, setChartConfig } = useChartSettingsContext();
  const [tableManager, setTableManager] = useState({ state: null });
  const defaultEditRecord = { dataObj: null, override: null, type: null, baseObj: null };
  const [editRecord, setEditRecord] = useState({
    [enumObjectTypes.holder]: defaultEditRecord,
    [enumObjectTypes.position]: defaultEditRecord
  });
  const [showRecordEditor, setShowRecordEditor] = useState(false);
  const [showPositionEditor, setShowPositionEditor] = useState(false);
  const [showCreatePosition, setShowCreatePosition] = useState(false);
  const [showTableEditor, setShowTableEditor] = useState(false);
  const [showTableManager, setShowTableManager] = useState(false);
  const [showCreateAppendTableModal, setShowCreateAppendTableModal] = useState(false);

  const { addToast } = useToast();

  const currentChartConfig = chartConfigRef.current;

  useTopic("SyncTableManager", tableMgr => {
    setTableManager({ state: tableMgr });
  });

  const onCloseRecordEditor = () => {
    setEditRecord(prevState => ({
      ...prevState,
      [enumObjectTypes.holder]: defaultEditRecord
    }));
    setShowRecordEditor(false);
  };

  const onClosePositionEditor = () => {
    setEditRecord(prevState => ({
      ...prevState,
      [enumObjectTypes.position]: defaultEditRecord
    }));
    setShowPositionEditor(false);
    setShowCreatePosition(false);
  };

  const getEditTarget = override => {
    return editRecord[override.SmartGetFieldValue("objType") || enumObjectTypes.holder];
  };

  const handleClose = override => {
    if (override.SmartGetFieldValue("objType") === enumObjectTypes.position) {
      onClosePositionEditor();
    } else {
      onCloseRecordEditor();
    }
  };

  const onCloseTableEditor = () => {
    setShowTableEditor(false);
    setTableManager({ state: null });
  };

  const onSaveTableEdit = () => {
    onCloseTableEditor();
  };

  const SendUpdate = async override => {
    let tableMgr = tableManager.state;
    const wsMgr = GetWSMgr();
    const appendTableID = tableMgr.fTableInfo["ID"];
    const overrideData = SerializeOverrideObj(override);
    let data = { ID: appendTableID, override: overrideData };
    if (override.fRollUp) {
      data["RollUp"] = true;
      data["chartID"] = currentChartConfig?.ID;
    }
    const { overrides, code, response } = await BuildAppendTableRFC(data, "newappendentry");
    if (code != 0) {
      addToast({ message: appstr("An error occurred while saving"), status: "error" });
    } else {
      addToast({ message: appstr("Changes have been saved"), status: "success" });
      tableMgr.LoadOverrides(overrides);
      setTableManager({ state: tableMgr });
    }
  };

  const onSaveRecordEdit = async data => {
    const { result, override, chartConfig } = data;
    const nextConfig = chartConfig ? { ...chartConfig } : {};
    override.SmartSetFieldValue("Comment", result.comment);

    const overrideType = override.SmartGetFieldValue("Type");
    if (overrideType === enumOverrideType.add && !override.SmartGetFieldValue("chartRefObj")) {
      override.SmartSetFieldValue("chartRefObj", result.changes);
    }

    override.fChanges[0] = QTableChange.FromAppendTableData(override, result.changes);
    await SendUpdate(override);

    // save table config changes
    if (data?.additionalFields?.length > 0 || data?.deletedFields?.length > 0) {
      const tableMgr = tableManager.state;
      const appendTable = structuredClone(tableMgr.fTableInfo);
      appendTable["additionalFields"] = data.additionalFields;
      appendTable["deletedFields"] = data.deletedFields;
      const wsMgr = GetWSMgr();
      const tableData = JSON.stringify(appendTable);
      const additionalFieldsRFC = new QManageTablesRFC();

      const callbackObj = {
        fTableMgr: tableMgr
      };

      additionalFieldsRFC.InitUpdateTables(callbackObj);
      const accountName = wsMgr.GetAccountName();
      const username = wsMgr.GetUserName();
      additionalFieldsRFC.SetParams(accountName, username, "saveAdditionalFields", tableData);
      Globals._rfcMgr.QueueRFC(additionalFieldsRFC);
    }

    // save config changes
    const nextSync = GetNeedRefreshDiagnostics(chartConfigRef.current);
    const configChanges = { ...nextConfig, ...nextSync };
    const { success } = await UpdateChartMetadata(
      currentChartConfig.ID,
      JSON.stringify(configChanges)
    );

    if (success) {
      const nextChartConfig = { ...chartConfigRef.current, ...configChanges };
      addToast({ message: appstr("Refresh chart notice"), status: "warning" });
      setChartConfig(nextChartConfig);
      chartConfigRef.current = nextChartConfig;
    } else {
      addToast({ message: appstr("An error occurred while saving"), status: "error" });
    }

    handleClose(override);
  };

  const recordEditorContextValue = {
    theData: editRecord,
    onCloseRecordEditor,
    onClosePositionEditor,
    onSaveRecordEdit,
    showRecordEditor,
    showPositionEditor,
    showCreatePosition
  };

  const onEditRecord = (overrideObj, dataObj, changeType, baseObj) => {
    const objType = overrideObj.SmartGetFieldValue("objType") || enumObjectTypes.holder;
    const override_id = overrideObj.SmartGetFieldValue("override_id");
    const isExistingOverride = override_id != null && override_id !== "";

    setEditRecord(prevState => ({
      ...prevState,
      [objType]: { dataObj, override: overrideObj, type: changeType, baseObj }
    }));

    if (objType === enumObjectTypes.position) {
      if (changeType === enumOverrideType.add && !isExistingOverride) {
        setShowCreatePosition(true);
      } else {
        setShowPositionEditor(true);
      }
    } else {
      setShowRecordEditor(true);
    }
  };

  const onLoadTable = (table, overrides, code) => {
    const wsMgr = GetWSMgr();
    // Create a clone of the WSMgr with updates to necessary properties
    const mockWsMgr = cloneDeep(wsMgr);
    const mockDocMgr = new QXDocMgr();
    mockDocMgr.InitDocMgr(mockWsMgr);
    mockWsMgr.fDocMgr = mockDocMgr;

    const mockDoc = mockWsMgr.InitializeDoc({}, false, false, true);
    const mockIWizData = new QIWizData();
    mockIWizData.InitIWizData(enumDataSource.serverchart, enumIWizDataSet.interactive);
    mockIWizData.SetIsServerChart(true);
    mockIWizData.SetBaseChartID(chartConfigRef.current.ID);
    mockIWizData.SetFieldMapping({ ...chartConfigRef.current.fields });
    mockDocMgr.SetIWizDataInteractive(mockIWizData);
    mockDoc.LoadLiveChartMetadata(chartConfigRef.current);
    mockWsMgr.SetDoc(mockDoc);

    const tableMgr = new QTableManager();
    tableMgr.InitTableManager(mockWsMgr, table, overrides, code == "00003");
    setTableManager({ state: tableMgr });
    setShowTableEditor(true);
  };

  const tableEditorContextValue = {
    onLoadTable,
    onEditRecord,
    onSaveTableEdit,
    onCloseTableEditor,
    showTableEditor
  };

  return (
    <TableManagerContext.Provider
      value={{
        tableManager,
        setTableManager,
        recordEditorContextValue,
        tableEditorContextValue,
        showTableManager,
        setShowTableManager,
        showCreateAppendTableModal,
        setShowCreateAppendTableModal
      }}
    >
      <GatedModals>
        <CreateAppendTableModal />
        <ManageAppendTablesModal />
        <AppendTableEditorModal />
        <AppendTableRecordEditorModal />
        <AppendTableCreatePositionModal />
        <AppendTablePositionEditorModal />
      </GatedModals>
      {children}
    </TableManagerContext.Provider>
  );
};

export const useTableManagerContext = () => React.useContext(TableManagerContext);

export default TableManagerProvider;
